import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Package, Plus, Save, X, Truck, Calendar, Hash, 
  Building2, FileText, CheckCircle, Clock, AlertTriangle,
  ChevronDown, ChevronUp, Edit, Trash2
} from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

const FurnitureOrderManager = ({ 
  quote, 
  isOpen, 
  onClose, 
  onSaved,
  isAdmin = false 
}) => {
  // State per gli ordini
  const [orders, setOrders] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState({});
  
  // State per nuovo ordine
  const [showNewOrderForm, setShowNewOrderForm] = useState(false);
  const [newOrder, setNewOrder] = useState({
    order_number: '',
    supplier_name: '',
    order_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Estrai tutti gli item dal preventivo (supporta sia sections che items legacy)
  const extractAllItems = useCallback((quoteData) => {
    const items = [];
    
    if (quoteData?.sections && quoteData.sections.length > 0) {
      quoteData.sections.forEach((section, sectionIndex) => {
        (section.items || []).forEach((item, itemIndex) => {
          items.push({
            ...item,
            sectionName: section.name || `Sezione ${sectionIndex + 1}`,
            sectionIndex,
            itemIndex,
            fullId: item.id || `${sectionIndex}-${itemIndex}`
          });
        });
      });
    } else if (quoteData?.items) {
      quoteData.items.forEach((item, index) => {
        items.push({
          ...item,
          sectionName: 'Articoli',
          sectionIndex: 0,
          itemIndex: index,
          fullId: item.id || `item-${index}`
        });
      });
    }
    
    return items;
  }, []);

  // Inizializza dati
  useEffect(() => {
    if (isOpen && quote) {
      setOrders(quote.orders || []);
      const items = extractAllItems(quote);
      setAllItems(items);
      setSelectedItems([]);
      setHasUnsavedChanges(false);
      setShowNewOrderForm(false);
      
      // Espandi tutti gli ordini di default
      const expanded = {};
      (quote.orders || []).forEach(order => {
        expanded[order.id] = true;
      });
      setExpandedOrders(expanded);
    }
  }, [isOpen, quote, extractAllItems]);

  // Gestione chiusura con warning
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  // Toggle selezione item
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
    setHasUnsavedChanges(true);
  };

  // Seleziona tutti gli item non ordinati
  const selectAllUnordered = () => {
    const unorderedIds = allItems
      .filter(item => !item.ordered && !item.order_id)
      .map(item => item.fullId);
    setSelectedItems(unorderedIds);
    setHasUnsavedChanges(true);
  };

  // Deseleziona tutti
  const deselectAll = () => {
    setSelectedItems([]);
  };

  // Crea nuovo ordine
  const handleCreateOrder = async () => {
    if (!newOrder.order_number || !newOrder.supplier_name) {
      toast.error('Inserisci numero ordine e fornitore');
      return;
    }
    
    if (selectedItems.length === 0) {
      toast.error('Seleziona almeno un articolo da includere nell\'ordine');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(
        `/api/furniture-quotes/${quote.id}/orders`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            withCredentials: true
          },
          body: JSON.stringify({
            ...newOrder,
            item_ids: selectedItems
          })
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore creazione ordine');
      }
      
      const result = await response.json();
      toast.success(`Ordine ${newOrder.order_number} creato con ${selectedItems.length} articoli`);
      
      // Aggiorna lo stato locale
      setOrders(result.quote.orders || []);
      setAllItems(extractAllItems(result.quote));
      setSelectedItems([]);
      setShowNewOrderForm(false);
      setNewOrder({
        order_number: '',
        supplier_name: '',
        order_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setHasUnsavedChanges(false);
      
      if (onSaved) onSaved(result.quote);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Elimina ordine
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo ordine? Gli articoli torneranno disponibili.')) {
      return;
    }
    
    try {
      const response = await fetch(
        `/api/furniture-quotes/${quote.id}/orders/${orderId}`,
        {
          method: 'DELETE',
          headers: {
            withCredentials: true
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Errore eliminazione ordine');
      }
      
      toast.success('Ordine eliminato');
      
      // Ricarica i dati
      const quoteResponse = await fetch(
        `/api/furniture-quotes/${quote.id}`,
        {
          headers: {
            withCredentials: true
          }
        }
      );
      
      if (quoteResponse.ok) {
        const updatedQuote = await quoteResponse.json();
        setOrders(updatedQuote.orders || []);
        setAllItems(extractAllItems(updatedQuote));
        if (onSaved) onSaved(updatedQuote);
      }
      
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Toggle espansione ordine
  const toggleOrderExpanded = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  // Calcola totale item selezionati
  const selectedTotal = selectedItems.reduce((sum, itemId) => {
    const item = allItems.find(i => i.fullId === itemId);
    if (item) {
      const subtotal = (item.quantity || 1) * (item.unit_price || 0);
      const discount = subtotal * ((item.discount_percentage || 0) / 100);
      return sum + (subtotal - discount);
    }
    return sum;
  }, 0);

  // Conta item non ordinati
  const unorderedItems = allItems.filter(item => !item.ordered && !item.order_id);
  const orderedItems = allItems.filter(item => item.ordered || item.order_id);

  if (!quote) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#FF7819]" />
              Gestione Ordini - {quote.quote_number}
            </DialogTitle>
            <DialogDescription>
              {quote.title} • {formatCurrency(quote.grand_total || 0)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Warning se non validato */}
            {!quote.admin_validated && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Preventivo non validato</p>
                  <p className="text-sm text-amber-700">
                    Il preventivo deve essere validato dall'admin prima di poter creare ordini.
                  </p>
                </div>
              </div>
            )}

            {/* Riepilogo */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-gray-50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-gray-700">{allItems.length}</p>
                  <p className="text-sm text-gray-500">Articoli Totali</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{orderedItems.length}</p>
                  <p className="text-sm text-green-700">Ordinati</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{unorderedItems.length}</p>
                  <p className="text-sm text-amber-700">Da Ordinare</p>
                </CardContent>
              </Card>
            </div>

            {/* Ordini esistenti */}
            {orders.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-[#3C3C3C] flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Ordini Creati ({orders.length})
                </h3>
                
                {orders.map((order) => {
                  const orderItems = allItems.filter(item => item.order_id === order.id);
                  const orderTotal = orderItems.reduce((sum, item) => {
                    const subtotal = (item.quantity || 1) * (item.unit_price || 0);
                    const discount = subtotal * ((item.discount_percentage || 0) / 100);
                    return sum + (subtotal - discount);
                  }, 0);
                  
                  return (
                    <Card key={order.id} className="border-green-200 bg-green-50/50">
                      <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleOrderExpanded(order.id)}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {expandedOrders[order.id] ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                            <div>
                              <CardTitle className="text-base flex items-center gap-2">
                                <Hash className="w-4 h-4 text-green-600" />
                                {order.order_number}
                                <Badge className="bg-green-100 text-green-700 border-green-300">
                                  {orderItems.length} articoli
                                </Badge>
                              </CardTitle>
                              <p className="text-sm text-gray-500">
                                {order.supplier_name} • {formatDate(order.order_date)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <p className="font-bold text-green-600">{formatCurrency(orderTotal)}</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteOrder(order.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      
                      {expandedOrders[order.id] && (
                        <CardContent className="pt-0">
                          <div className="space-y-2 mt-2">
                            {orderItems.map((item, idx) => (
                              <div 
                                key={item.fullId || idx}
                                className="flex items-start justify-between gap-3 p-2 bg-white rounded border text-sm"
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium whitespace-pre-wrap break-words leading-snug">{item.description || 'Articolo'}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">{item.sectionName}</p>
                                </div>
                                <div className="text-right shrink-0 pt-0.5">
                                  <p className="whitespace-nowrap">{item.quantity} {item.unit_measure || 'cad'} × {formatCurrency(item.unit_price || 0)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                          {order.notes && (
                            <p className="text-sm text-gray-600 mt-3 italic">Note: {order.notes}</p>
                          )}
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Sezione creazione nuovo ordine */}
            {quote.admin_validated && unorderedItems.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold text-[#3C3C3C] flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Articoli da Ordinare ({unorderedItems.length})
                    {selectedItems.length > 0 && (
                      <span
                        className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-[#FF7819]/10 text-[#FF7819] text-xs font-semibold"
                        data-testid="furniture-order-selected-counter"
                      >
                        {selectedItems.length} selezionati
                      </span>
                    )}
                  </h3>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllUnordered}
                      data-testid="furniture-order-select-all-btn"
                    >
                      Seleziona Tutti
                    </Button>
                    {selectedItems.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={deselectAll}
                        data-testid="furniture-order-deselect-all-btn"
                      >
                        Deseleziona
                      </Button>
                    )}
                  </div>
                </div>

                {/* Lista articoli non ordinati - layout card multilinea leggibile */}
                <Card>
                  <CardContent className="p-0 max-h-[420px] overflow-y-auto">
                    {unorderedItems.map((item, idx) => {
                      const isSelected = selectedItems.includes(item.fullId);
                      const lineTotal = (item.quantity || 0) * (item.unit_price || 0) * (1 - (item.discount_percentage || 0) / 100);
                      return (
                        <div
                          key={item.fullId || idx}
                          className={`flex items-start gap-3 p-3 border-b last:border-b-0 cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-[#FF7819]/10 border-l-4 border-l-[#FF7819]'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => toggleItemSelection(item.fullId)}
                          data-testid={`furniture-order-item-row-${item.fullId}`}
                        >
                          <div className="pt-1 shrink-0">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleItemSelection(item.fullId)}
                              data-testid={`furniture-order-item-checkbox-${item.fullId}`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            {/* Riga 1: descrizione multilinea (no truncate) */}
                            <p className="font-medium text-[#3C3C3C] whitespace-pre-wrap break-words text-sm leading-snug">
                              {item.description || 'Articolo senza descrizione'}
                            </p>
                            {/* Riga 2: metadati - sezione, q.tà, U.M., prezzo, totale */}
                            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                              <span className="text-gray-500">{item.sectionName}</span>
                              <span className="text-gray-300">•</span>
                              <span><span className="text-gray-500">Q.tà</span> <span className="font-medium text-gray-800">{item.quantity}</span> {item.unit_measure || 'cad'}</span>
                              <span className="text-gray-300">•</span>
                              <span><span className="text-gray-500">Prezzo</span> <span className="font-medium text-gray-800">{formatCurrency(item.unit_price || 0)}</span></span>
                              {item.discount_percentage > 0 && (
                                <>
                                  <span className="text-gray-300">•</span>
                                  <span className="text-red-600 font-medium">-{item.discount_percentage}%</span>
                                </>
                              )}
                              <span className="text-gray-300">•</span>
                              <span><span className="text-gray-500">Totale</span> <span className="font-semibold text-[#FF7819]">{formatCurrency(lineTotal)}</span></span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Form nuovo ordine */}
                {selectedItems.length > 0 && (
                  <Card className="border-[#FF7819]/30 bg-[#FF7819]/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Plus className="w-4 h-4 text-[#FF7819]" />
                        Crea Ordine per {selectedItems.length} articoli
                        <span className="text-[#FF7819] font-bold ml-auto">
                          {formatCurrency(selectedTotal)}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">
                            <Hash className="w-3 h-3" /> Numero Ordine *
                          </Label>
                          <Input
                            value={newOrder.order_number}
                            onChange={(e) => setNewOrder({...newOrder, order_number: e.target.value})}
                            placeholder="Es: ORD-2025-001"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> Fornitore *
                          </Label>
                          <Input
                            value={newOrder.supplier_name}
                            onChange={(e) => setNewOrder({...newOrder, supplier_name: e.target.value})}
                            placeholder="Nome fornitore"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Data Ordine
                          </Label>
                          <Input
                            type="date"
                            value={newOrder.order_date}
                            onChange={(e) => setNewOrder({...newOrder, order_date: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">
                            <FileText className="w-3 h-3" /> Note
                          </Label>
                          <Input
                            value={newOrder.notes}
                            onChange={(e) => setNewOrder({...newOrder, notes: e.target.value})}
                            placeholder="Note ordine (opzionale)"
                          />
                        </div>
                      </div>
                      
                      <Button
                        className="w-full btn-primary"
                        onClick={handleCreateOrder}
                        disabled={saving || !newOrder.order_number || !newOrder.supplier_name}
                      >
                        {saving ? (
                          <>
                            <Clock className="w-4 h-4 mr-2 animate-spin" />
                            Salvataggio...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Crea Ordine
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Messaggio se tutti gli articoli sono ordinati */}
            {quote.admin_validated && unorderedItems.length === 0 && allItems.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Tutti gli articoli sono stati ordinati!</p>
                  <p className="text-sm text-green-700">
                    {orders.length} ordine/i creato/i per {orderedItems.length} articoli.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning salvataggio non effettuato */}
      <AlertDialog open={showUnsavedWarning} onOpenChange={setShowUnsavedWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Modifiche non salvate
            </AlertDialogTitle>
            <AlertDialogDescription>
              Hai selezionato degli articoli ma non hai ancora creato l'ordine.
              Vuoi uscire senza salvare?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowUnsavedWarning(false)}>
              Torna indietro
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmClose}
              className="bg-red-500 hover:bg-red-600"
            >
              Esci senza salvare
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default FurnitureOrderManager;
